import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ExternalLink, TrendingUp, TrendingDown, Heart, Check, X, DollarSign, Percent, Calculator, ChevronDown, ChevronUp, Info, ArrowUpCircle, ArrowDownCircle, MinusCircle, Database, Hammer, Home, FileText, FileSpreadsheet, Download, Sparkles, ChevronLeft, ChevronRight, Images, Briefcase, Lock, BarChart3, Copy, Share2, Save, Users, Lightbulb, AlertTriangle } from "lucide-react";
import ShareDeal from "./ShareDeal";
import AnalysisSaveCard from "./AnalysisSaveCard";
import EliteDeepDiveCredit from "./EliteDeepDiveCredit";
import { useAnalysisSession } from "../hooks/useAnalysisSession";
import { toast } from "sonner";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableRow, TableHeader, TableHead } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { formatCurrency, formatPercent, PropertyTypeIcon, API } from "../lib/helpers";
import { DealScoreGauge, DealScoreBadge } from "./DealScoreGauge";
import { RentCastUnlockCard, DataSourcesBadge } from "./analysis/RentTransparency";
import { DataSourcesFooter, ConfidenceChip } from "./analysis/DataSourceProvenance";
import { StrategyMatchPanel } from "./StrategyMatchPanel";
import { LenderGradeMetrics, TenYearProjection } from "./analysis/InvestmentMetrics";
import { ExpensesSection } from "./analysis/ExpensesSection";

// Inline FAQ context link — used to link metrics to their explainer guides
const LearnLink = ({ to, children }) => (
  <Link to={to} className="inline-flex items-center gap-1 text-[10px] text-emerald-500/80 hover:text-emerald-500 transition-colors" data-testid={`learn-link-${to.split('/').pop()}`}>
    <Info className="w-3 h-3" />{children}
  </Link>
);

// Photo Gallery Component
const PhotoGallery = ({ photos, mainImage }) => {
  const [idx, setIdx] = useState(0);
  const allPhotos = [...(photos || [])];
  if (mainImage && !allPhotos.includes(mainImage)) allPhotos.unshift(mainImage);
  if (allPhotos.length === 0) return null;

  return (
    <div className="mb-4" data-testid="photo-gallery">
      <div className="relative rounded-xl overflow-hidden bg-black/50" style={{ height: 280 }}>
        <img
          src={allPhotos[idx]}
          alt={`Property photo ${idx + 1}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {allPhotos.length > 1 && (
          <>
            <button
              onClick={() => setIdx((idx - 1 + allPhotos.length) % allPhotos.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIdx((idx + 1) % allPhotos.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
              data-testid="gallery-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-black/60 rounded-full px-2.5 py-1">
              <Images className="w-3 h-3 text-white/80" />
              <span className="text-[10px] text-white/80 font-mono">{idx + 1}/{allPhotos.length}</span>
            </div>
          </>
        )}
      </div>
      {allPhotos.length > 1 && (
        <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">
          {allPhotos.slice(0, 10).map((url, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`flex-shrink-0 w-14 h-10 rounded-md overflow-hidden border-2 transition-colors ${i === idx ? 'border-emerald-500' : 'border-transparent opacity-60 hover:opacity-100'}`} data-testid={`gallery-thumb-${i}`}>
              <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Export Buttons Component
const ExportButtons = ({ property, analysis, isPro, onUpgrade }) => {
  const [exporting, setExporting] = useState(null);

  if (!isPro) {
    return (
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="text-xs h-7 px-2 opacity-60" data-testid="export-pdf-locked" onClick={() => onUpgrade && onUpgrade("export")}>
          <Lock className="w-3 h-3" /><span className="ml-1 hidden sm:inline">PDF</span>
        </Button>
        <Button variant="outline" size="sm" className="text-xs h-7 px-2 opacity-60" data-testid="export-excel-locked" onClick={() => onUpgrade && onUpgrade("export")}>
          <Lock className="w-3 h-3" /><span className="ml-1 hidden sm:inline">Excel</span>
        </Button>
      </div>
    );
  }

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await axios.post(`${API}/export/${format}`, {
        property, analysis, format,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      a.download = `RealixData_${(property.address || 'property').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={!!exporting} className="text-xs h-7 px-2" data-testid="export-pdf-btn">
        {exporting === 'pdf' ? <Download className="w-3 h-3 animate-bounce" /> : <FileText className="w-3 h-3" />}
        <span className="ml-1 hidden sm:inline">PDF</span>
      </Button>
      <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={!!exporting} className="text-xs h-7 px-2" data-testid="export-excel-btn">
        {exporting === 'excel' ? <Download className="w-3 h-3 animate-bounce" /> : <FileSpreadsheet className="w-3 h-3" />}
        <span className="ml-1 hidden sm:inline">Excel</span>
      </Button>
    </div>
  );
};

// Score Factors Panel - "Why This Score?"
const ScoreFactorsPanel = ({ scoreFactors, dealScore }) => {
  if (!scoreFactors || scoreFactors.length === 0) return null;
  const positives = scoreFactors.filter(f => f.type === "positive");
  const negatives = scoreFactors.filter(f => f.type === "negative");
  const neutrals = scoreFactors.filter(f => f.type === "neutral");

  return (
    <Card className="bg-secondary/30 border-border" data-testid="score-factors-panel">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Info className="w-4 h-4 text-muted-foreground" />Why This Score?
          </CardTitle>
          <DealScoreBadge score={dealScore} tone="app" />
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-2">
        {positives.map((f, i) => (
          <div key={`pos-${i}`} className="flex items-start gap-2.5 py-1.5 px-3 rounded-md bg-emerald-500/5 border border-emerald-500/15">
            <ArrowUpCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-foreground">{f.text}</span>
            {f.weight === "high" && <Badge className="ml-auto bg-emerald-500/15 text-emerald-500 border-0 text-[9px] px-1.5 py-0 flex-shrink-0">Strong</Badge>}
          </div>
        ))}
        {negatives.map((f, i) => (
          <div key={`neg-${i}`} className="flex items-start gap-2.5 py-1.5 px-3 rounded-md bg-red-500/5 border border-red-500/15">
            <ArrowDownCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-foreground">{f.text}</span>
            {f.weight === "high" && <Badge className="ml-auto bg-red-500/15 text-red-500 border-0 text-[9px] px-1.5 py-0 flex-shrink-0">Critical</Badge>}
          </div>
        ))}
        {neutrals.map((f, i) => (
          <div key={`neu-${i}`} className="flex items-start gap-2.5 py-1.5 px-3 rounded-md bg-secondary/50 border border-border">
            <MinusCircle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <span className="text-sm text-muted-foreground">{f.text}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// Market Conditions Section
const MarketConditionsSection = ({ analysis }) => {
  const mc = analysis.market_conditions;
  const my = analysis.market_yield;
  const rv = analysis.rent_validation;
  const adj = analysis.market_score_adjustment || 0;
  const factors = analysis.market_score_factors || [];

  if (!mc && !my) return (
    <div className="p-6 text-center text-sm text-muted-foreground" data-testid="market-no-data">
      Market data not available for this ZIP code.
    </div>
  );

  const tempColor = {
    "Hot": "text-red-500 bg-red-500/10 border-red-500/30",
    "Warm": "text-orange-500 bg-orange-500/10 border-orange-500/30",
    "Balanced": "text-amber-500 bg-amber-500/10 border-amber-500/30",
    "Cool": "text-blue-500 bg-blue-500/10 border-blue-500/30",
    "Buyer's Market": "text-emerald-500 bg-emerald-500/10 border-emerald-500/30",
  }[mc?.market_temperature] || "text-muted-foreground bg-secondary/40 border-border";

  return (
    <div className="space-y-4" data-testid="market-conditions-section">
      {mc && (
        <Card className="bg-secondary/30 border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Market Conditions (ZIP {mc.zip_code})</CardTitle>
              <Badge className={`text-xs border ${tempColor}`}>{mc.market_temperature || "N/A"}</Badge>
            </div>
            {mc.data_source && (
              <p className="text-[10px] text-muted-foreground mt-0.5">Source: {mc.data_source === "redfin_mls" ? "Redfin MLS" : mc.data_source} {mc.data_date ? `(${mc.data_date})` : ""}</p>
            )}
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {mc.median_sale_price && (
                <div className="p-2.5 rounded-lg bg-background border border-border">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Median Sale</p>
                  <p className="font-mono text-sm font-bold">{formatCurrency(mc.median_sale_price)}</p>
                </div>
              )}
              {mc.median_days_on_market && (
                <div className="p-2.5 rounded-lg bg-background border border-border">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Days on Market</p>
                  <p className="font-mono text-sm font-bold">{mc.median_days_on_market} days</p>
                </div>
              )}
              {mc.inventory_months != null && (
                <div className="p-2.5 rounded-lg bg-background border border-border">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Inventory</p>
                  <p className="font-mono text-sm font-bold">{mc.inventory_months.toFixed(1)} months</p>
                </div>
              )}
              {mc.yoy_price_change != null && (
                <div className={`p-2.5 rounded-lg border ${mc.yoy_price_change >= 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Price YoY</p>
                  <p className={`font-mono text-sm font-bold flex items-center gap-1 ${mc.yoy_price_change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {mc.yoy_price_change >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {(mc.yoy_price_change * 100).toFixed(1)}%
                  </p>
                </div>
              )}
              {mc.mom_price_change != null && (
                <div className={`p-2.5 rounded-lg border ${mc.mom_price_change >= 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Price MoM</p>
                  <p className={`font-mono text-sm font-bold flex items-center gap-1 ${mc.mom_price_change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {mc.mom_price_change >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {(mc.mom_price_change * 100).toFixed(1)}%
                  </p>
                </div>
              )}
              {mc.new_listings_30d && (
                <div className="p-2.5 rounded-lg bg-background border border-border">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">New Listings (30d)</p>
                  <p className="font-mono text-sm font-bold">{mc.new_listings_30d}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {my && (
        <Card className="bg-secondary/30 border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Rental Yield Analysis</CardTitle>
              <Badge className={`text-xs border ${
                my.yield_rating === "Excellent" ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" :
                my.yield_rating === "Good" ? "text-blue-500 bg-blue-500/10 border-blue-500/30" :
                "text-amber-500 bg-amber-500/10 border-amber-500/30"
              }`}>{my.yield_rating || "N/A"}</Badge>
            </div>
            {my.data_source && (
              <p className="text-[10px] text-muted-foreground mt-0.5">Source: {my.data_source === "hud_fmr" ? "HUD Fair Market Rent" : my.data_source}</p>
            )}
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-2.5 rounded-lg bg-background border border-border">
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Gross Yield</p>
                <p className="font-mono text-sm font-bold text-emerald-500">{my.gross_yield_pct}%</p>
              </div>
              <div className="p-2.5 rounded-lg bg-background border border-border">
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Market Rent</p>
                <p className="font-mono text-sm font-bold">{formatCurrency(my.market_rent_estimate)}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-background border border-border">
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Rent-to-Price</p>
                <p className="font-mono text-sm font-bold">{my.rent_to_price_ratio}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {rv && rv.flagged && (
        <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="rent-validation-warning">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-amber-600">Rent Estimate Warning</p>
              <p className="text-xs text-muted-foreground mt-0.5">{rv.message}</p>
            </div>
          </div>
        </div>
      )}

      {factors.length > 0 && (
        <Card className="bg-secondary/30 border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold">Market Impact on Deal Score ({adj >= 0 ? "+" : ""}{adj})</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-1.5">
            {factors.map((f, i) => (
              <div key={i} className={`flex items-center justify-between py-1.5 px-3 rounded-md border ${
                f.type === "positive" ? "bg-emerald-500/5 border-emerald-500/15" :
                f.type === "negative" ? "bg-red-500/5 border-red-500/15" :
                "bg-secondary/50 border-border"
              }`}>
                <span className="text-xs">{f.text}</span>
                <span className={`font-mono text-xs font-bold ${f.points > 0 ? "text-emerald-500" : f.points < 0 ? "text-red-500" : "text-muted-foreground"}`}>
                  {f.points > 0 ? "+" : ""}{f.points}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Assumptions Disclosure Panel
const AssumptionsPanel = ({ assumptions, dataSources }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (!assumptions || Object.keys(assumptions).length === 0) return null;

  return (
    <Card className="bg-secondary/20 border-border/50" data-testid="assumptions-panel">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/30 transition-colors rounded-lg"
        data-testid="assumptions-toggle"
      >
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Data Sources & Assumptions</span>
          {dataSources && (
            <span className="text-[10px] text-amber-500 font-medium">
              {Object.values(dataSources).filter(s => s.is_estimated).length} estimated values
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {isOpen && (
        <CardContent className="px-4 pb-4 pt-0 space-y-4">
          {dataSources && Object.keys(dataSources).length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Data Sources</p>
              <div className="space-y-1.5">
                {Object.entries(dataSources).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between py-1 px-2 rounded bg-secondary/30">
                    <span className="text-xs text-muted-foreground capitalize">{key}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground">{val.label}</span>
                      <DataSourcesBadge source={val} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Calculation Assumptions</p>
            <div className="space-y-1.5">
              {Object.entries(assumptions).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between py-1 px-2 rounded bg-secondary/30">
                  <span className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-foreground font-mono">{val}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};


// Fix & Flip — imported from separate component
import FixFlipSection from "./FixFlipSection";

// Airbnb / STR Analysis Section (Interactive Calculator)
const AirbnbSection = ({ airbnb, ltrRent, monthlyMortgage }) => {
  const [nightlyRate, setNightlyRate] = useState(airbnb?.nightly_rate || 120);
  const [occupancyPct, setOccupancyPct] = useState(airbnb?.occupancy_pct || 60);
  const [mgmtPct, setMgmtPct] = useState(airbnb?.expenses?.management_pct || 20);
  const [cleaningPerStay, setCleaningPerStay] = useState(airbnb?.expenses?.cleaning_per_stay || 90);

  if (!airbnb) return null;

  // Recalculate
  const bookedNights = Math.round(30 * (occupancyPct / 100));
  const grossMonthly = Math.round(nightlyRate * bookedNights);
  const avgStayLength = 3.5;
  const cleaningsPerMonth = Math.round(bookedNights / avgStayLength);
  const cleaningMonthly = Math.round(cleaningPerStay * cleaningsPerMonth);
  const platformFee = Math.round(grossMonthly * 0.03);
  const managementFee = Math.round(grossMonthly * (mgmtPct / 100));
  const suppliesMonthly = airbnb.expenses?.supplies || 80;
  const furnishingMonthly = airbnb.expenses?.furnishing || 250;
  const insuranceMonthly = airbnb.expenses?.insurance || 0;
  const taxesMonthly = airbnb.expenses?.taxes || 0;
  const totalExpenses = cleaningMonthly + platformFee + managementFee + suppliesMonthly + furnishingMonthly + insuranceMonthly + taxesMonthly;
  const mortgage = monthlyMortgage || 0;
  const netMonthly = grossMonthly - totalExpenses - mortgage;

  // LTR comparison
  const ltrExpenses = Math.round(insuranceMonthly + taxesMonthly + (ltrRent * 0.08) + (ltrRent * 0.05));
  const ltrNet = Math.round(ltrRent - ltrExpenses - mortgage);
  const monthlyDiff = netMonthly - ltrNet;
  const isBetter = netMonthly > ltrNet;

  const startup = airbnb.startup_costs || {};

  return (
    <div className="space-y-4" data-testid="airbnb-section">
      {/* Editable Inputs */}
      <Card className="bg-secondary/20 border-border">
        <CardContent className="p-3 sm:p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-3">Adjust Assumptions</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Nightly Rate ($)</Label>
              <Input type="number" value={nightlyRate} onChange={e => setNightlyRate(Number(e.target.value))} className="h-8 text-sm font-mono bg-secondary/30 border-border mt-1" data-testid="airbnb-input-rate" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Occupancy (%)</Label>
              <Input type="number" value={occupancyPct} min={0} max={100} onChange={e => setOccupancyPct(Number(e.target.value))} className="h-8 text-sm font-mono bg-secondary/30 border-border mt-1" data-testid="airbnb-input-occupancy" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Management Fee (%)</Label>
              <Input type="number" value={mgmtPct} step={1} onChange={e => setMgmtPct(Number(e.target.value))} className="h-8 text-sm font-mono bg-secondary/30 border-border mt-1" data-testid="airbnb-input-mgmt" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Cleaning/Stay ($)</Label>
              <Input type="number" value={cleaningPerStay} onChange={e => setCleaningPerStay(Number(e.target.value))} className="h-8 text-sm font-mono bg-secondary/30 border-border mt-1" data-testid="airbnb-input-cleaning" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="p-3 rounded-lg bg-secondary/40 border border-border">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Nightly Rate</p>
          <p className="font-mono text-sm sm:text-lg font-bold text-foreground">{formatCurrency(nightlyRate)}</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/40 border border-border">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Occupancy</p>
          <p className="font-mono text-sm sm:text-lg font-bold text-foreground">{occupancyPct}%</p>
          <p className="text-[9px] text-muted-foreground">{bookedNights} nights/mo</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/40 border border-border">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Gross/mo</p>
          <p className="font-mono text-sm sm:text-lg font-bold text-emerald-500">{formatCurrency(grossMonthly)}</p>
        </div>
        <div className={`p-3 rounded-lg border ${netMonthly >= 0 ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-red-500/5 border-red-500/30'}`}>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Net/mo</p>
          <p className={`font-mono text-sm sm:text-lg font-bold ${netMonthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(netMonthly)}</p>
        </div>
      </div>

      {/* STR vs LTR Comparison */}
      <Card className={`border ${isBetter ? 'bg-violet-500/5 border-violet-500/30' : 'bg-blue-500/5 border-blue-500/30'}`} data-testid="str-ltr-comparison">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Airbnb vs Long-Term Rental</p>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-[9px] text-muted-foreground uppercase mb-1">Airbnb Net</p>
              <p className={`font-mono text-sm font-bold ${netMonthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(netMonthly)}/mo</p>
            </div>
            <div>
              <p className="text-[9px] text-muted-foreground uppercase mb-1">LTR Net</p>
              <p className={`font-mono text-sm font-bold ${ltrNet >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(ltrNet)}/mo</p>
            </div>
            <div>
              <p className="text-[9px] text-muted-foreground uppercase mb-1">Difference</p>
              <p className={`font-mono text-sm font-bold ${monthlyDiff >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{monthlyDiff >= 0 ? '+' : ''}{formatCurrency(monthlyDiff)}</p>
            </div>
          </div>
          <div className="mt-3 text-center">
            <Badge className={`text-xs px-3 py-1 ${isBetter ? 'bg-violet-500/15 text-violet-400 border-violet-500/20' : 'bg-blue-500/15 text-blue-400 border-blue-500/20'}`}>
              {isBetter ? 'Airbnb earns more' : 'Long-term rental earns more'} ({monthlyDiff >= 0 ? '+' : ''}{formatCurrency(monthlyDiff)}/mo)
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* STR Expense Breakdown */}
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-2 pt-3 px-4"><CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">STR Monthly Expenses</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableBody>
              <TableRow className="bg-emerald-500/5"><TableCell className="text-emerald-500 text-sm font-semibold">Gross Income ({bookedNights} nights x {formatCurrency(nightlyRate)})</TableCell><TableCell className="font-mono text-right text-emerald-500 text-sm font-bold">{formatCurrency(grossMonthly)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Cleaning ({formatCurrency(cleaningPerStay)}/stay)</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(cleaningMonthly)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Platform Fee (3%)</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(platformFee)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Management ({mgmtPct}%)</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(managementFee)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Supplies & Consumables</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(suppliesMonthly)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Furnishing (amortized 3yr)</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(furnishingMonthly)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Insurance</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(insuranceMonthly)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Property Taxes</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(taxesMonthly)}</TableCell></TableRow>
              <TableRow className="bg-secondary/50 border-t-2"><TableCell className="text-foreground text-sm font-semibold">Total Expenses</TableCell><TableCell className="font-mono text-right text-foreground text-sm font-bold">-{formatCurrency(totalExpenses)}</TableCell></TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Startup Costs */}
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-2 pt-3 px-4"><CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Startup Costs (One-Time)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableBody>
              <TableRow><TableCell className="text-muted-foreground text-sm">Furnishing & Setup</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(startup.furnishing)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Professional Photography</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(startup.photography)}</TableCell></TableRow>
              <TableRow><TableCell className="text-muted-foreground text-sm">Initial Supplies</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(startup.initial_supplies)}</TableCell></TableRow>
              <TableRow className="bg-secondary/50 border-t-2"><TableCell className="font-bold text-sm">Total Startup</TableCell><TableCell className="font-mono text-right font-bold text-sm">{formatCurrency(startup.total)}</TableCell></TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Seized property detail view
const SeizedPropertyDetail = ({ property }) => (
  <div className="p-6">
    <DialogHeader className="mb-6">
      <div>
        <DialogTitle className="text-2xl font-bold text-foreground mb-2">{property.address}</DialogTitle>
        <DialogDescription className="sr-only">Seized property details for {property.address}</DialogDescription>
        <p className="text-muted-foreground flex items-center gap-1.5">
          <MapPin className="w-5 h-5 text-emerald-500" />{property.city}, {property.state} {property.zip_code}
        </p>
      </div>
    </DialogHeader>
    <div className="flex gap-2 mb-6 flex-wrap">
      <Badge className="bg-orange-500/15 text-orange-500 border-orange-500/30">Seized Property</Badge>
      <Badge className="bg-slate-700/50 text-slate-300">U.S. Treasury</Badge>
      {property.sale_type && <Badge variant="secondary">{property.sale_type === 'online_auction' ? 'Online Auction' : 'Broker Sale'}</Badge>}
      {property.sale_number && <Badge variant="secondary">Sale #{property.sale_number}</Badge>}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <Card className="bg-secondary/30 border-border p-4 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Auction Details</h3>
        {property.auction_date && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Auction Date</span><span className="font-semibold text-orange-500">{property.auction_date}</span></div>}
        {property.starting_bid != null && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Starting Bid</span><span className="font-mono font-bold text-xl">{formatCurrency(property.starting_bid)}</span></div>}
        {property.deposit != null && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Deposit Required</span><span className="font-mono font-semibold">{formatCurrency(property.deposit)}</span></div>}
        {property.inspections && <div><span className="text-muted-foreground text-sm block">Inspections</span><span className="text-sm">{property.inspections}</span></div>}
      </Card>
      <Card className="bg-secondary/30 border-border p-4 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Property Details</h3>
        {property.bedrooms > 0 && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Bedrooms</span><span className="font-semibold">{property.bedrooms}</span></div>}
        {property.bathrooms > 0 && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Bathrooms</span><span className="font-semibold">{property.bathrooms}</span></div>}
        {property.sqft > 0 && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Sq. Ft.</span><span className="font-semibold">{property.sqft.toLocaleString()}</span></div>}
        {property.lot_size && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Lot Size</span><span className="font-semibold">{property.lot_size}</span></div>}
        {property.year_built > 0 && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Year Built</span><span className="font-semibold">{property.year_built}</span></div>}
        {property.county && <div className="flex justify-between"><span className="text-muted-foreground text-sm">County</span><span className="font-semibold">{property.county}</span></div>}
        {property.zoning && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Zoning</span><span className="font-semibold">{property.zoning}</span></div>}
        {property.utilities && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Utilities</span><span className="font-semibold">{property.utilities}</span></div>}
        {property.taxes_annual && <div className="flex justify-between"><span className="text-muted-foreground text-sm">Annual Taxes</span><span className="font-mono font-semibold">{formatCurrency(property.taxes_annual)}</span></div>}
      </Card>
    </div>
    {property.description && (
      <Card className="bg-secondary/30 border-border p-4 mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">Description</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{property.description}</p>
      </Card>
    )}
    <div className="flex gap-3 flex-wrap">
      {property.listing_url && (
        <a href={property.listing_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-colors" data-testid="view-official-btn">
          <ExternalLink className="w-4 h-4" />View Official Listing
        </a>
      )}
      {property.flyer_url && (
        <a href={property.flyer_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg text-sm font-semibold transition-colors">
          <ExternalLink className="w-4 h-4" />Property Flyer
        </a>
      )}
      {property.floor_plan_url && (
        <a href={property.floor_plan_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg text-sm font-semibold transition-colors">
          <ExternalLink className="w-4 h-4" />Floor Plan
        </a>
      )}
      {property.gallery_url && (
        <a href={property.gallery_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg text-sm font-semibold transition-colors">
          <ExternalLink className="w-4 h-4" />Photo Gallery
        </a>
      )}
    </div>
  </div>
);

// Investment Summary Panel - top-level KPI cards
const InvestmentSummaryPanel = ({ analysis, property }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4 sm:mb-6" data-testid="investment-summary-panel">
    <div className="p-2.5 sm:p-3 rounded-lg bg-secondary/40 border border-border">
      <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Purchase Price</p>
      <p className="font-mono text-sm sm:text-lg font-bold text-foreground">{formatCurrency(analysis.purchase_price)}</p>
    </div>
    <div className="p-2.5 sm:p-3 rounded-lg bg-secondary/40 border border-border">
      <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Monthly Rent</p>
      <p className="font-mono text-sm sm:text-lg font-bold text-emerald-500">{formatCurrency(analysis.estimated_monthly_rent)}</p>
    </div>
    <div className="p-2.5 sm:p-3 rounded-lg bg-secondary/40 border border-border">
      <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
        {analysis.pmi_monthly > 0 ? "Mortgage (P&I + PMI)" : "Mortgage (P&I)"}
      </p>
      <p className="font-mono text-sm sm:text-lg font-bold text-red-400">{formatCurrency(analysis.monthly_mortgage)}</p>
      {analysis.pmi_monthly > 0 && (
        <p className="text-[9px] text-muted-foreground/70 mt-0.5">
          Incl. {formatCurrency(analysis.pmi_monthly)} PMI
        </p>
      )}
    </div>
    <div className={`p-2.5 sm:p-3 rounded-lg border ${analysis.cash_flow_monthly >= 0 ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-red-500/5 border-red-500/30'}`}>
      <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Cash Flow/mo</p>
      <p className={`font-mono text-sm sm:text-lg font-bold ${analysis.cash_flow_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.cash_flow_monthly)}</p>
      <LearnLink to="/faq/how-realixdata-calculates-cash-flow">How we calculate this</LearnLink>
    </div>
    <div className={`p-2.5 sm:p-3 rounded-lg border ${analysis.cap_rate >= 5 ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-secondary/40 border-border'}`}>
      <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Cap Rate</p>
      <p className={`font-mono text-sm sm:text-lg font-bold ${analysis.cap_rate >= 5 ? 'text-emerald-500' : 'text-foreground'}`}>{formatPercent(analysis.cap_rate)}</p>
      <LearnLink to="/faq/what-is-a-good-cap-rate">What's a good cap rate?</LearnLink>
    </div>
    <div className={`p-3 rounded-lg border ${
      analysis.deal_score >= 75 ? 'bg-emerald-500/10 border-emerald-500/30' :
      analysis.deal_score >= 50 ? 'bg-amber-500/10 border-amber-500/30' :
      'bg-red-500/10 border-red-500/30'
    }`}>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Deal Score</p>
      <DealScoreGauge score={analysis.deal_score} size="sm" tone="app" />
      {analysis.market_score_adjustment != null && analysis.market_score_adjustment !== 0 && (
        <p className={`text-[9px] font-mono mt-0.5 ${analysis.market_score_adjustment > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
          Mkt {analysis.market_score_adjustment > 0 ? '+' : ''}{analysis.market_score_adjustment}
        </p>
      )}
      <LearnLink to="/faq/understanding-deal-scores">How scores work</LearnLink>
    </div>
  </div>
);

// Mortgage & Purchase section
const MortgageSection = ({ analysis, params, onParamsChange, onSaveParams }) => (
  <div className="space-y-4" data-testid="mortgage-section">
    {/* Adjustable Parameters */}
    <div className="p-4 rounded-lg bg-secondary/20 border border-border">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Down Payment %</Label>
          <Input type="number" value={params.down_payment_percent} onChange={(e) => onParamsChange({ ...params, down_payment_percent: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} max={100} />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Closing Costs %</Label>
          <Input type="number" value={params.closing_costs_percent} onChange={(e) => onParamsChange({ ...params, closing_costs_percent: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} max={10} />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Repair Costs</Label>
          <Input type="number" value={params.repair_costs} onChange={(e) => onParamsChange({ ...params, repair_costs: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Loan Term (Yrs)</Label>
          <Input type="number" value={params.loan_term_years} onChange={(e) => onParamsChange({ ...params, loan_term_years: parseInt(e.target.value) || 30 })} className="font-mono mt-1 h-8 text-sm" min={1} max={40} />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Interest Rate %</Label>
          <Input type="number" value={params.interest_rate} onChange={(e) => onParamsChange({ ...params, interest_rate: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} max={20} step={0.1} />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Loan Points</Label>
          <Input type="number" value={params.loan_points || 0} onChange={(e) => onParamsChange({ ...params, loan_points: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} max={10} step={0.25} />
        </div>
      </div>
      {onSaveParams && (
        <div className="flex justify-end mt-3">
          <Button variant="outline" size="sm" onClick={onSaveParams} className="text-xs h-7" data-testid="save-analysis-params-btn">
            <Save className="w-3 h-3 mr-1" /> Save Changes
          </Button>
        </div>
      )}
    </div>

    {/* Purchase Breakdown Table */}
    <Card className="bg-secondary/30 border-border">
      <CardContent className="p-0">
        <Table>
          <TableBody>
            <TableRow><TableCell className="text-muted-foreground text-sm">Purchase Price</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(analysis.purchase_price)}</TableCell></TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Down Payment ({analysis.down_payment_percent}%)</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(analysis.down_payment_amount)}</TableCell></TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Loan Amount</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(analysis.loan_amount)}</TableCell></TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Closing Costs ({analysis.closing_costs_percent}%)</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(analysis.closing_costs_amount)}</TableCell></TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Repair Costs</TableCell><TableCell className="font-mono text-right text-sm">{formatCurrency(analysis.repair_costs)}</TableCell></TableRow>
            <TableRow className="bg-secondary/50"><TableCell className="font-semibold text-foreground text-sm">Total Capital Needed</TableCell><TableCell className="font-mono text-right font-bold text-sm">{formatCurrency(analysis.total_capital_needed)}</TableCell></TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Monthly Mortgage (P&I)</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">{formatCurrency(analysis.monthly_mortgage)}</TableCell></TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  </div>
);

const ROISection = ({ analysis, property, isElite }) => (
  <div className="space-y-4" data-testid="roi-section">
    {/* Key Return Metrics */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="text-center p-4 bg-secondary/40 rounded-lg border border-border">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">NOI (Yearly)</p>
        <p className={`font-mono text-xl font-bold ${analysis.noi_yearly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.noi_yearly)}</p>
      </div>
      <div className="text-center p-4 bg-secondary/40 rounded-lg border border-border">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Cash Flow (Yearly)</p>
        <p className={`font-mono text-xl font-bold ${analysis.cash_flow_yearly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.cash_flow_yearly)}</p>
      </div>
      <div className="text-center p-4 bg-secondary/40 rounded-lg border border-border">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Cap Rate</p>
        <p className={`font-mono text-xl font-bold ${analysis.cap_rate >= 5 ? 'text-emerald-500' : 'text-foreground'}`}>{formatPercent(analysis.cap_rate)}</p>
      </div>
      <div className="text-center p-4 bg-secondary/40 rounded-lg border border-border">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Cash on Cash</p>
        <p className={`font-mono text-xl font-bold ${analysis.cash_on_cash_return >= 8 ? 'text-emerald-500' : analysis.cash_on_cash_return >= 0 ? 'text-amber-500' : 'text-red-500'}`}>{formatPercent(analysis.cash_on_cash_return)}</p>
      </div>
    </div>

    {/* Investment Rules */}
    <div className="grid grid-cols-2 gap-3">
      <div className={`p-4 rounded-lg ${analysis.one_percent_rule ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
        <div className="flex items-center gap-2 mb-2">
          {analysis.one_percent_rule ? <Check className="w-4 h-4 text-emerald-500" /> : <X className="w-4 h-4 text-red-500" />}
          <span className={`text-sm font-semibold ${analysis.one_percent_rule ? 'text-emerald-500' : 'text-red-500'}`}>1% Rule</span>
        </div>
        <p className="text-xs text-muted-foreground mb-1">Monthly rent should be 1%+ of purchase price</p>
        <div className="font-mono text-xs">
          <span className="text-muted-foreground">Rent: </span><span className="text-foreground">{formatCurrency(analysis.estimated_monthly_rent)}</span>
          <span className="text-muted-foreground"> vs 1%: </span><span className="text-foreground">{formatCurrency(analysis.one_percent_value)}</span>
        </div>
        <div className="mt-1.5"><LearnLink to="/faq/1-percent-rule-real-estate-explained">Learn about the 1% rule</LearnLink></div>
      </div>
      <div className={`p-4 rounded-lg ${analysis.fifty_percent_rule ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
        <div className="flex items-center gap-2 mb-2">
          {analysis.fifty_percent_rule ? <Check className="w-4 h-4 text-emerald-500" /> : <X className="w-4 h-4 text-red-500" />}
          <span className={`text-sm font-semibold ${analysis.fifty_percent_rule ? 'text-emerald-500' : 'text-red-500'}`}>50% Rule</span>
        </div>
        <p className="text-xs text-muted-foreground mb-1">Operating expenses ≤ 50% of gross income</p>
        <div className="font-mono text-xs">
          <span className="text-muted-foreground">Expense Ratio: </span>
          <span className={analysis.fifty_percent_rule ? 'text-emerald-500' : 'text-red-500'}>{formatPercent(analysis.expense_ratio)}</span>
        </div>
      </div>
    </div>

    {/* ── Tier 1: Lender-Grade Metrics (free for everyone) ── */}
    <LenderGradeMetrics analysis={analysis} />

    {/* ── Tier 2: 10-Year Projection (Elite-only with locked preview) ── */}
    <TenYearProjection
      propertyId={property?.id || analysis?.property_id}
    />

    {/* BRRRR Score if applicable */}
    {analysis.brrrr_score >= 40 && (
      <div className={`p-4 rounded-lg border ${analysis.brrrr_score >= 60 ? 'bg-violet-500/10 border-violet-500/30' : 'bg-secondary/40 border-border'}`}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-semibold text-violet-400">BRRRR Potential</span>
            <p className="text-xs text-muted-foreground mt-0.5">Buy, Rehab, Rent, Refinance, Repeat</p>
            <LearnLink to="/faq/brrrr-strategy-explained">Learn the BRRRR strategy</LearnLink>
          </div>
          <span className={`font-mono text-2xl font-bold ${analysis.brrrr_score >= 60 ? 'text-violet-500' : 'text-muted-foreground'}`}>{analysis.brrrr_score}<span className="text-xs text-muted-foreground font-normal">/100</span></span>
        </div>
      </div>
    )}

    {/* Market Data */}
    {analysis.zip_data && (
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold">Market Data for {analysis.zip_data.zip_code}</CardTitle>
          <CardDescription className="text-xs">{analysis.zip_data.city}, {analysis.zip_data.state_abbr || analysis.zip_data.state}</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Avg 1BR</p>
              <p className="font-mono text-sm font-semibold">{formatCurrency(analysis.zip_data.avg_rent_1br)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Avg 2BR</p>
              <p className="font-mono text-sm font-semibold">{formatCurrency(analysis.zip_data.avg_rent_2br)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Avg 3BR</p>
              <p className="font-mono text-sm font-semibold">{formatCurrency(analysis.zip_data.avg_rent_3br)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Tax Rate</p>
              <p className="font-mono text-sm font-semibold">{formatPercent(analysis.zip_data.property_tax_rate)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    )}
  </div>
);

// AI Insights Section
const AIInsightsSection = ({ property }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/properties/${property.id}/ai-insights`);
      setInsights(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate AI insights");
    } finally {
      setLoading(false);
    }
  };

  if (!insights && !loading && !error) {
    return (
      <div className="text-center py-8" data-testid="ai-insights-prompt">
        <Sparkles className="w-8 h-8 mx-auto text-emerald-500 mb-3" />
        <h3 className="text-sm font-semibold mb-1">AI Investment Analysis</h3>
        <p className="text-xs text-muted-foreground mb-4">Get GPT-4o powered insights on this property's investment potential</p>
        <Button onClick={fetchInsights} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ai-generate-btn">
          <Sparkles className="w-4 h-4 mr-1.5" />Generate AI Insights
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-8" data-testid="ai-loading">
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="w-4 h-4 animate-pulse text-emerald-500" />Analyzing property with AI...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6" data-testid="ai-error">
        <p className="text-sm text-red-400 mb-3">{error}</p>
        <Button onClick={fetchInsights} size="sm" variant="outline" data-testid="ai-retry-btn">Retry</Button>
      </div>
    );
  }

  if (insights?.error) {
    return (
      <div className="text-center py-6" data-testid="ai-error">
        <p className="text-sm text-red-400 mb-3">{insights.error}</p>
        <Button onClick={fetchInsights} size="sm" variant="outline">Retry</Button>
      </div>
    );
  }

  const verdictColor = {
    "Strong Buy": "text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
    "Buy": "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    "Hold": "text-amber-400 bg-amber-500/10 border-amber-500/20",
    "Caution": "text-orange-400 bg-orange-500/10 border-orange-500/20",
    "Avoid": "text-red-400 bg-red-500/10 border-red-500/20",
  }[insights.verdict] || "text-muted-foreground bg-secondary/30 border-border";

  return (
    <div className="space-y-4" data-testid="ai-insights-result">
      {/* Verdict & Confidence */}
      <div className={`flex items-center justify-between p-4 rounded-lg border ${verdictColor}`}>
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5" />
          <div>
            <span className="text-lg font-bold">{insights.verdict}</span>
            <p className="text-xs opacity-70">AI Confidence: {insights.confidence}%</p>
          </div>
        </div>
        <Badge className={`${verdictColor} text-xs`}>{insights.confidence}/100</Badge>
      </div>

      {/* Summary */}
      <p className="text-sm text-foreground leading-relaxed">{insights.summary}</p>

      {/* Strengths & Risks */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <h4 className="text-xs font-semibold text-emerald-500 uppercase mb-2">Strengths</h4>
          {insights.strengths?.map((s, i) => (
            <div key={i} className="flex items-start gap-2 mb-1.5">
              <ArrowUpCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-foreground">{s}</span>
            </div>
          ))}
        </div>
        <div>
          <h4 className="text-xs font-semibold text-red-400 uppercase mb-2">Risks</h4>
          {insights.risks?.map((r, i) => (
            <div key={i} className="flex items-start gap-2 mb-1.5">
              <ArrowDownCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-foreground">{r}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Strategy Fit */}
      {insights.strategy_fit && (
        <Card className="bg-secondary/30 border-border">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-semibold">Strategy Fit</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-2">
            {Object.entries(insights.strategy_fit).map(([key, val]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, " ")}</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(val.score / 10) * 100}%` }} />
                  </div>
                  <span className="text-xs font-mono w-6 text-right">{val.score}/10</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Negotiation Tip */}
      {insights.negotiation_tip && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h4 className="text-xs font-semibold text-blue-400 mb-1">Negotiation Tip</h4>
          <p className="text-xs text-foreground">{insights.negotiation_tip}</p>
        </div>
      )}

      {/* Comparable Insight */}
      {insights.comparable_insight && (
        <p className="text-[11px] text-muted-foreground italic">{insights.comparable_insight}</p>
      )}
    </div>
  );
};

// Owner Insights Section (Realie data)
const OwnerInsightsSection = ({ realieData }) => {
  if (!realieData) return (
    <div className="text-center py-6 text-sm text-muted-foreground" data-testid="owner-insights-empty">
      <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
      <p>Owner data not available for this property</p>
      <p className="text-[10px] mt-1">Powered by Realie.ai — data loads during analysis</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="owner-insights-section">
      {(realieData.property_class_label || realieData.use_code) && (
        <Card className="bg-secondary/30 border-border" data-testid="owner-insights-classification">
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Property Classification</h4>
            <div className="text-xs space-y-1">
              {realieData.property_class_label && (
                <div><span className="text-muted-foreground">Class</span><p className="font-semibold" data-testid="property-class-label">{realieData.property_class_label}</p></div>
              )}
              {realieData.use_code && (
                <div className="text-[10px] text-muted-foreground">Parcel use code: <span className="font-mono">{realieData.use_code}</span></div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {(realieData.foreclosure_code || realieData.foreclosure_date || realieData.foreclosure_case_num || realieData.auction_date) && (
        <Card className="bg-orange-500/5 border-orange-500/40" data-testid="owner-insights-distressed">
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5 text-orange-400">
              <AlertTriangle className="w-3.5 h-3.5" />Distressed Status
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {realieData.foreclosure_case_num && (
                <div><span className="text-muted-foreground">Case Number</span><p className="font-mono font-semibold" data-testid="foreclosure-case-num">{realieData.foreclosure_case_num}</p></div>
              )}
              {realieData.foreclosure_code && (
                <div><span className="text-muted-foreground">Code</span><p className="font-mono font-semibold">{realieData.foreclosure_code}</p></div>
              )}
              {realieData.foreclosure_file_date && (
                <div><span className="text-muted-foreground">Filed</span><p className="font-semibold">{realieData.foreclosure_file_date}</p></div>
              )}
              {realieData.foreclosure_date && (
                <div><span className="text-muted-foreground">Recorded</span><p className="font-semibold">{realieData.foreclosure_date}</p></div>
              )}
              {realieData.auction_date && (
                <div className="col-span-2"><span className="text-muted-foreground">Scheduled Auction</span><p className="font-semibold text-orange-400" data-testid="foreclosure-auction-date">{realieData.auction_date}</p></div>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              Distressed listings often require all-cash purchases, may have title issues,
              and are usually sold as-is. Always verify with the county recorder.
            </p>
          </CardContent>
        </Card>
      )}

      {realieData.owner_name && (
        <Card className="bg-secondary/30 border-border">
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Owner Information</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-muted-foreground">Name</span><p className="font-semibold">{realieData.owner_name}</p></div>
              {realieData.equity_estimate != null && <div><span className="text-muted-foreground">Est. Equity</span><p className="font-mono font-semibold text-emerald-500">{formatCurrency(realieData.equity_estimate)}</p></div>}
              {realieData.lien_balance != null && <div><span className="text-muted-foreground">Lien Balance</span><p className="font-mono font-semibold text-red-400">{formatCurrency(realieData.lien_balance)}</p></div>}
            </div>
          </CardContent>
        </Card>
      )}

      {realieData.actual_annual_tax && (
        <Card className="bg-secondary/30 border-border">
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" />Tax & Valuation</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-muted-foreground">Annual Tax</span><p className="font-mono font-semibold">{formatCurrency(realieData.actual_annual_tax)}/yr</p></div>
              {realieData.assessed_value && <div><span className="text-muted-foreground">Assessed Value</span><p className="font-mono font-semibold">{formatCurrency(realieData.assessed_value)}</p></div>}
              {realieData.market_value && <div><span className="text-muted-foreground">Market Value</span><p className="font-mono font-semibold">{formatCurrency(realieData.market_value)}</p></div>}
              {realieData.avm_estimate && (
                <div>
                  <span className="text-muted-foreground">AVM Estimate</span>
                  <p className="font-mono font-semibold">{formatCurrency(realieData.avm_estimate)}</p>
                  {realieData.avm_min && realieData.avm_max && (
                    <p className="text-[9px] text-muted-foreground">{formatCurrency(realieData.avm_min)} - {formatCurrency(realieData.avm_max)}</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {realieData.transfer_history && realieData.transfer_history.length > 0 && (
        <Card className="bg-secondary/30 border-border">
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" />Sale History</h4>
            <div className="space-y-1.5">
              {realieData.transfer_history.slice(0, 5).map((t, i) => (
                <div key={i} className="flex justify-between items-center text-xs py-1 border-b border-border/30 last:border-0">
                  <span className="text-muted-foreground">{t.transferDate || 'Unknown date'}</span>
                  <span className="font-mono font-semibold">{t.transferPrice ? formatCurrency(t.transferPrice) : '—'}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {realieData.assessment_history && realieData.assessment_history.length > 0 && (
        <Card className="bg-secondary/30 border-border">
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Assessment History</h4>
            <div className="space-y-1.5">
              {realieData.assessment_history.slice(0, 4).map((a, i) => (
                <div key={i} className="flex justify-between items-center text-xs py-1 border-b border-border/30 last:border-0">
                  <span className="text-muted-foreground">{a.assessedYear || 'N/A'}</span>
                  <span className="font-mono font-semibold">{a.totalAssessedValue ? formatCurrency(a.totalAssessedValue) : '—'}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <p className="text-[9px] text-muted-foreground text-center">Data provided by Realie.ai</p>
    </div>
  );
};

// Deal Improvements Section
const ImprovementsSection = ({ improvements }) => {
  if (!improvements || improvements.length === 0) return (
    <div className="text-center py-6 text-sm text-muted-foreground" data-testid="improvements-empty">
      <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-30" />
      <p>This deal already looks strong — no improvements needed!</p>
    </div>
  );

  const iconMap = {
    dollar: DollarSign,
    percent: Percent,
    trending_up: TrendingUp,
    wallet: Briefcase,
    user: Users,
    home_plus: Home,
  };

  const priorityConfig = {
    high: { color: "border-emerald-500/30 bg-emerald-500/5", badge: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", label: "High Impact" },
    medium: { color: "border-amber-500/30 bg-amber-500/5", badge: "bg-amber-500/10 text-amber-500 border-amber-500/20", label: "Medium Impact" },
    low: { color: "border-muted bg-muted/10", badge: "bg-muted text-muted-foreground border-border", label: "Consider" },
  };

  return (
    <div className="space-y-2.5" data-testid="improvements-section">
      <p className="text-xs text-muted-foreground mb-3">
        Concrete changes that could make this deal work better for you:
      </p>
      {improvements.map((imp, i) => {
        const cfg = priorityConfig[imp.priority] || priorityConfig.low;
        const Icon = iconMap[imp.icon] || Lightbulb;
        return (
          <Card key={i} className={`border ${cfg.color}`} data-testid={`improvement-${imp.type}`}>
            <CardContent className="p-3">
              <div className="flex items-start gap-2.5">
                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-secondary/40 flex items-center justify-center mt-0.5">
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h4 className="text-xs font-bold">{imp.title}</h4>
                    <Badge className={`text-[9px] px-1.5 py-0 ${cfg.badge}`}>{cfg.label}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{imp.description}</p>
                  <p className="text-[10px] font-semibold text-emerald-500 mt-1">{imp.impact}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Main AnalysisModal
const AnalysisModal = ({ property, analysis, isOpen, onClose, onParamsChange, params, isSaved, onSaveToggle, onSaveParams, subscription, onUpgrade, isAuthenticated }) => {
  const [addingToPortfolio, setAddingToPortfolio] = useState(false);
  const [inPortfolio, setInPortfolio] = useState(false);
  const [summCopied, setSummCopied] = useState(false);
  const { analysisId, trackFunnelEvent } = useAnalysisSession();
  const isPro = subscription?.is_paid || false;
  const isElite = subscription?.is_elite || false;

  const handleCopySummary = () => {
    if (!analysis) return;
    const isTop = analysis.deal_score >= 85;
    const mc = analysis.market_conditions;
    const summary = [
      `${property.address}, ${property.city}, ${property.state} ${property.zip_code}`,
      ``,
      `Price: ${formatCurrency(property.price)}`,
      `Rent: ${formatCurrency(analysis.estimated_monthly_rent || 0)}/mo`,
      `Cash Flow: ${formatCurrency(analysis.cash_flow_monthly || 0)}/mo`,
      `Cap Rate: ${analysis.cap_rate?.toFixed(1)}%`,
      `Deal Score: ${analysis.deal_score}/100`,
      isTop ? `--- Top 5% Deal ---` : null,
      mc ? `Market: ${mc.market_temperature || 'N/A'} | Median: ${formatCurrency(mc.median_sale_price)} | ${mc.median_days_on_market} DOM` : null,
      ``,
      `Analyzed with RealixData`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(summary);
    setSummCopied(true);
    toast.success("Deal summary copied!");
    setTimeout(() => setSummCopied(false), 2000);
  };

  useEffect(() => {
    if (property?.id && isOpen) {
      axios.get(`${API}/portfolio`).then(res => {
        const ids = res.data.properties.map(p => p.property_id);
        setInPortfolio(ids.includes(property.id));
      }).catch(() => {});
    }
  }, [property?.id, isOpen]);

  const handleAddToPortfolio = async () => {
    if (!property || !analysis) return;
    if (!isElite) {
      onUpgrade?.("elite:portfolio");
      return;
    }
    setAddingToPortfolio(true);
    try {
      await axios.post(`${API}/portfolio`, {
        property_id: property.id,
        address: property.address,
        city: property.city,
        state: property.state,
        zip_code: property.zip_code,
        purchase_price: analysis.purchase_price,
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        sqft: property.sqft,
        property_type: property.property_type,
        image_url: property.image_url,
        monthly_rent: analysis.estimated_monthly_rent,
        monthly_mortgage: analysis.monthly_mortgage,
        monthly_expenses: analysis.total_monthly_expenses - analysis.monthly_mortgage,
      });
      setInPortfolio(true);
      toast.success("Added to your portfolio!");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Already in your portfolio");
        setInPortfolio(true);
      } else {
        toast.error("Failed to add to portfolio");
      }
    } finally {
      setAddingToPortfolio(false);
    }
  };

  if (!property) return null;

  // Seized property modal
  if (property.is_seized && !analysis) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl w-[95vw] sm:w-auto max-h-[95vh] sm:max-h-[90vh] bg-card border-border p-0 overflow-hidden" data-testid="analysis-modal">
          <ScrollArea className="max-h-[90vh]">
            <SeizedPropertyDetail property={property} />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    );
  }

  if (!analysis) return null;

  const ProLockedTab = ({ label, desc, onUpgrade: handleUpgrade }) => (
    <Card className="bg-secondary/10 border-border border-dashed" data-testid="pro-locked-tab">
      <CardContent className="p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-3">
          <Lock className="w-5 h-5 text-amber-500" />
        </div>
        <h4 className="font-semibold text-sm mb-1">{label}</h4>
        <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">{desc}</p>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => handleUpgrade && handleUpgrade("analysis")}>
          Upgrade to Pro
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-card border-border p-0 overflow-hidden" data-testid="analysis-modal">
        <ScrollArea className="max-h-[95vh] sm:max-h-[90vh]">
          <div className="p-3 sm:p-6">
            {/* Header */}
            <DialogHeader className="mb-4">
              {/* Top toolbar row - compact actions */}
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-1.5">
                  <ExportButtons property={property} analysis={analysis} isPro={isPro} onUpgrade={onUpgrade} />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {isPro ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="text-xs h-7 px-2.5" data-testid="modal-share-dropdown-btn">
                          <Share2 className="w-3 h-3 mr-1" /> Share
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={handleCopySummary} data-testid="share-menu-copy-summary">
                          <Copy className="w-3.5 h-3.5 mr-2" />
                          {summCopied ? "Copied!" : "Copy Deal Summary"}
                        </DropdownMenuItem>
                        <ShareDeal property={property} analysis={analysis} variant="dropdown" />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Button variant="outline" size="sm" className="text-xs h-7 px-2.5 opacity-60" onClick={onUpgrade} data-testid="modal-share-locked-btn">
                      <Lock className="w-3 h-3 mr-1" /> Share <Badge className="ml-1 text-[8px] px-1 py-0 bg-amber-500/15 text-amber-500 border-0">Pro</Badge>
                    </Button>
                  )}
                  <Button variant={isSaved ? "default" : "outline"} size="sm" onClick={() => onSaveToggle(property.id)} className={`text-xs h-7 ${isSaved ? "bg-red-500 hover:bg-red-600" : ""}`} data-testid="modal-save-btn">
                    <Heart className={`w-3 h-3 mr-1 ${isSaved ? 'fill-white' : ''}`} />{isSaved ? 'Saved' : 'Save'}
                  </Button>
                  <Button
                    variant={inPortfolio ? "default" : "outline"}
                    size="sm"
                    onClick={handleAddToPortfolio}
                    disabled={addingToPortfolio || inPortfolio}
                    className={`text-xs h-7 relative ${inPortfolio ? "bg-emerald-600 hover:bg-emerald-600 text-white" : !isElite ? "opacity-70" : ""}`}
                    data-testid="modal-portfolio-btn"
                  >
                    {!isElite && !inPortfolio ? (
                      <>
                        <Lock className="w-3 h-3 mr-1" />
                        Portfolio
                        <Badge className="ml-1 text-[8px] px-1 py-0 bg-amber-500/20 text-amber-500 border-0">Elite</Badge>
                      </>
                    ) : (
                      <>
                        <Briefcase className="w-3 h-3 mr-1" />
                        {inPortfolio ? 'In Portfolio' : addingToPortfolio ? '...' : 'Add to Portfolio'}
                      </>
                    )}
                  </Button>
                  <Badge className={`text-[10px] px-2 py-1 ${analysis.is_income_producing ? 'badge-success' : 'badge-danger'}`}>
                    {analysis.is_income_producing ? <><TrendingUp className="w-3 h-3 mr-1" />Profitable</> : <><TrendingDown className="w-3 h-3 mr-1" />Not Profitable</>}
                  </Badge>
                </div>
              </div>
              {/* Property info - address prominent */}
              <div>
                <DialogTitle className="text-xl sm:text-2xl font-bold text-foreground mb-1" data-testid="modal-property-address">{property.address}</DialogTitle>
                <DialogDescription className="sr-only">Investment analysis for {property.address}</DialogDescription>
                <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1.5 mb-2">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-emerald-500" />{property.city}, {property.state} {property.zip_code}
                </p>
                <div className="flex gap-1 flex-wrap">
                  <Badge variant="secondary" className="text-[9px] sm:text-[10px]">
                    <PropertyTypeIcon type={property.property_type} />
                    <span className="ml-1 capitalize">{property.property_type.replace('_', ' ')}</span>
                  </Badge>
                  {analysis.property_sub_type && <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[9px] sm:text-[10px]">{analysis.property_sub_type}</Badge>}
                  <Badge variant="secondary" className="text-[9px] sm:text-[10px]">{property.bedrooms}bd</Badge>
                  <Badge variant="secondary" className="text-[9px] sm:text-[10px]">{property.bathrooms}ba</Badge>
                  <Badge variant="secondary" className="text-[9px] sm:text-[10px]">{(property.sqft || 0).toLocaleString()}sf</Badge>
                  {property.units > 1 && <Badge variant="secondary" className="text-[9px] sm:text-[10px]">{property.units} units</Badge>}
                  {analysis?.data_confidence && (
                    <ConfidenceChip confidence={analysis.data_confidence} size="xs" className="ml-1" />
                  )}
                </div>
                <div className="mt-2">
                  <DataSourcesFooter dataSources={analysis?.data_sources} property={property} />
                </div>
              </div>
            </DialogHeader>

            {/* Photo Gallery */}
            <PhotoGallery photos={analysis.photos} mainImage={property.image_url} />

            {/* Investment Summary Panel */}
            <InvestmentSummaryPanel analysis={analysis} property={property} />

            {/* Score Factors - "Why This Score?" - locked explanation for free users */}
            {isPro ? (
              <ScoreFactorsPanel scoreFactors={analysis.score_factors} dealScore={analysis.deal_score} />
            ) : (
              <Card className="bg-secondary/20 border-border relative overflow-hidden" data-testid="score-factors-locked">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold">Why This Score? ({analysis.deal_score}/100)</h4>
                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px]">Pro</Badge>
                  </div>
                  <div className="blur-sm select-none pointer-events-none h-20 bg-secondary/10 rounded" />
                  <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-[2px]">
                    <Button variant="outline" size="sm" className="text-xs border-amber-500/30 text-amber-500 hover:bg-amber-500/10" onClick={() => onUpgrade && onUpgrade("analysis")}>
                      Unlock Full Breakdown
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Strategy Match — deterministic fit vs the user's saved criteria */}
            {analysis.strategy_match && (
              <StrategyMatchPanel match={analysis.strategy_match} strategyName={analysis.strategy_name_applied} />
            )}

            {/* Tabbed Sections */}
            <Tabs defaultValue="market" className="w-full">
              <TabsList className="grid w-full grid-cols-10 mb-4">
                <TabsTrigger value="market" className="text-[10px] sm:text-xs gap-1" data-testid="tab-market">
                  <BarChart3 className="w-3.5 h-3.5" /><span className="hidden sm:inline">Market</span><span className="sm:hidden">Mkt</span>
                </TabsTrigger>
                <TabsTrigger value="improve" className="text-[10px] sm:text-xs gap-1" data-testid="tab-improve">
                  <Lightbulb className="w-3.5 h-3.5" /><span className="hidden sm:inline">Improve</span><span className="sm:hidden">Fix</span>
                </TabsTrigger>
                <TabsTrigger value="ai" className="text-[10px] sm:text-xs gap-1" data-testid="tab-ai">
                  <Sparkles className="w-3.5 h-3.5" /><span className="hidden sm:inline">AI</span><span className="sm:hidden">AI</span>
                </TabsTrigger>
                <TabsTrigger value="expenses" className="text-[10px] sm:text-xs gap-1" data-testid="tab-expenses">
                  <DollarSign className="w-3.5 h-3.5" /><span className="hidden sm:inline">Expenses</span><span className="sm:hidden">$</span>
                </TabsTrigger>
                <TabsTrigger value="mortgage" className="text-[10px] sm:text-xs gap-1" data-testid="tab-mortgage">
                  <Calculator className="w-3.5 h-3.5" /><span className="hidden sm:inline">Mortgage</span><span className="sm:hidden">Mtg</span>
                </TabsTrigger>
                <TabsTrigger value="roi" className="text-[10px] sm:text-xs gap-1 relative" data-testid="tab-roi">
                  <Percent className="w-3.5 h-3.5" /><span className="hidden sm:inline">ROI</span><span className="sm:hidden">ROI</span>
                  {!isPro && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />}
                </TabsTrigger>
                <TabsTrigger value="flip" className="text-[10px] sm:text-xs gap-1 relative" data-testid="tab-flip">
                  <Hammer className="w-3.5 h-3.5" /><span className="hidden sm:inline">Flip</span><span className="sm:hidden">Flip</span>
                  {!isPro && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />}
                </TabsTrigger>
                <TabsTrigger value="airbnb" className="text-[10px] sm:text-xs gap-1 relative" data-testid="tab-airbnb">
                  <Home className="w-3.5 h-3.5" /><span className="hidden sm:inline">Airbnb</span><span className="sm:hidden">STR</span>
                  {!isPro && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />}
                </TabsTrigger>
                <TabsTrigger value="owner" className="text-[10px] sm:text-xs gap-1 relative" data-testid="tab-owner">
                  <Users className="w-3.5 h-3.5" /><span className="hidden sm:inline">Owner</span><span className="sm:hidden">Own</span>
                  {!isPro && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />}
                </TabsTrigger>
                <TabsTrigger value="source" className="text-[10px] sm:text-xs gap-1" data-testid="tab-source">
                  <ExternalLink className="w-3.5 h-3.5" /><span className="hidden sm:inline">Source</span><span className="sm:hidden">Src</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="market">
                <MarketConditionsSection analysis={analysis} />
              </TabsContent>

              <TabsContent value="improve">
                <ImprovementsSection improvements={analysis.improvements} />
              </TabsContent>

              <TabsContent value="ai">
                {isPro ? <AIInsightsSection property={property} /> : (
                  <ProLockedTab label="AI Deal Insights" desc="Get AI-powered analysis of this property's investment potential." onUpgrade={onUpgrade} />
                )}
              </TabsContent>

              <TabsContent value="expenses">
                <ExpensesSection analysis={analysis} property={property} params={params} onParamsChange={onParamsChange} isPro={isPro} isElite={isElite} isAuthed={!!subscription} onUpgrade={onUpgrade} />
              </TabsContent>

              <TabsContent value="mortgage">
                <MortgageSection analysis={analysis} params={params} onParamsChange={onParamsChange} onSaveParams={onSaveParams} />
              </TabsContent>

              <TabsContent value="roi">
                {isPro ? <ROISection analysis={analysis} property={property} isElite={isElite} /> : (
                  <ProLockedTab label="Advanced ROI Analysis" desc="Cash-on-cash return, 5-year equity buildup, and detailed ROI projections." onUpgrade={onUpgrade} />
                )}
              </TabsContent>

              <TabsContent value="flip">
                {isPro ? <FixFlipSection fixFlip={analysis.fix_flip} params={params} onParamsChange={onParamsChange} /> : (
                  <ProLockedTab label="Fix & Flip Calculator" desc="ARV analysis, rehab cost estimator, and flip profit projections." onUpgrade={onUpgrade} />
                )}
              </TabsContent>

              <TabsContent value="airbnb">
                {isPro ? <AirbnbSection airbnb={analysis.airbnb} ltrRent={analysis.estimated_monthly_rent} monthlyMortgage={analysis.monthly_mortgage} /> : (
                  <ProLockedTab label="Airbnb Comparison" desc="Short-term rental income estimate vs. long-term rental with expense breakdown." onUpgrade={onUpgrade} />
                )}
              </TabsContent>

              <TabsContent value="owner">
                {isPro ? <OwnerInsightsSection realieData={analysis.realie_data} /> : (
                  <ProLockedTab label="Owner Insights" desc="Owner name, equity estimate, lien data, sale history, and tax assessments powered by Realie.ai." onUpgrade={onUpgrade} />
                )}
              </TabsContent>

              <TabsContent value="source">
                <div className="space-y-4" data-testid="source-info-card">
                  <Card className="bg-secondary/30 border-border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary" className="text-xs">
                            {property.is_seized ? 'U.S. Treasury' : property.listing_status === 'recently_sold' ? 'Recently Sold' : 'MLS'}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {property.is_seized ? 'Federal seized property auction' : property.listing_source || 'Zillow MLS Listing'}
                          </span>
                        </div>
                        {property.listing_url && (
                          <a href={property.listing_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity" data-testid="view-original-listing">
                            <ExternalLink className="w-4 h-4" />View Original
                          </a>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-border/40">
                        <DataSourcesFooter dataSources={analysis?.data_sources} property={property} />
                      </div>
                    </CardContent>
                  </Card>
                  <Button className="w-full" onClick={() => window.open(property.listing_url, '_blank')} data-testid="view-listing-btn">
                    <ExternalLink className="w-4 h-4 mr-2" />View on {property.listing_source}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            {/* Data Sources & Assumptions */}
            <AssumptionsPanel assumptions={analysis.assumptions} dataSources={analysis.data_sources} />

            {/* Lead-magnet CTA — anonymous visitors only. Authenticated
                users already have the "save" heart button in the header
                and don't need an email capture. */}
            {!isAuthenticated && analysisId && (
              <AnalysisSaveCard
                analysisId={analysisId}
                propertyAddress={property?.address}
                trackFunnelEvent={trackFunnelEvent}
              />
            )}

            {/* Elite Deep-Dive credit — surfaces the 1-per-cycle
                entitlement or the $5 add-on price. Renders nothing for
                non-Elite users, so anonymous tripwire is unaffected. */}
            {isAuthenticated && (
              <EliteDeepDiveCredit
                analysisId={analysisId}
                isAuthenticated={isAuthenticated}
              />
            )}

            {/* Disclaimer */}
            <div className="mt-6 p-3 border border-amber-500/20 rounded-lg bg-amber-500/5">
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                <span className="font-semibold text-amber-500/80">Disclaimer:</span> Estimates based on current market data and default assumptions. Actual investment performance may vary. RealixData does not provide financial, investment, legal, or tax advice. Users should independently verify all information and perform their own due diligence before making any investment decisions. <a href="/legal/investment-disclaimer" className="text-emerald-500 hover:underline">Full disclaimer</a>
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AnalysisModal;
